import React, { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { useSetInitialPassword } from '@hooks'
import { Text } from '../atoms'
import { Button, TextInput } from '../molecules'

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

export type CreatePasswordFormProps = {
  /** Called once the password is saved. */
  onSaved: () => void
  /** Shows the discreet "Plus tard" action when provided. */
  onLater?: () => void
}

/**
 * First password for an account created through an event code. Used after the
 * code sign-up / code login and in Settings. The API only accepts it while the
 * account still has no password.
 */
export const CreatePasswordForm = ({
  onSaved,
  onLater,
}: CreatePasswordFormProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t } = useLanguage()
  const setPassword = useSetInitialPassword()
  const [password, setValue] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const tooShort = password.length < PASSWORD_MIN_LENGTH
  const mismatch = password !== confirmation
  const ruleFailed = submitted && tooShort

  const submit = () => {
    setSubmitted(true)
    if (tooShort || mismatch || setPassword.isPending) return
    setPassword.mutate({ newPassword: password }, { onSuccess: onSaved })
  }

  return (
    <View style={styles.container}>
      <TextInput
        testID="create-password-input"
        placeholder={t('createPassword.password')}
        value={password}
        onChangeText={(value) => setValue(value.slice(0, PASSWORD_MAX_LENGTH))}
        secureTextEntry
        iconRight="eye"
        hasError={ruleFailed}
        autoComplete="new-password"
      />
      <Text type="bodyExtraSmall" color={ruleFailed ? theme.colors.error : theme.colors.greyBlue}>
        {t('createPassword.rule', { min: PASSWORD_MIN_LENGTH })}
      </Text>
      <TextInput
        testID="create-password-confirm"
        placeholder={t('createPassword.confirm')}
        value={confirmation}
        onChangeText={(value) => setConfirmation(value.slice(0, PASSWORD_MAX_LENGTH))}
        secureTextEntry
        iconRight="eye"
        hasError={submitted && mismatch}
        errorMessage={submitted && mismatch ? t('createPassword.mismatch') : undefined}
        autoComplete="new-password"
        onSubmitEditing={submit}
      />
      <View style={styles.actions}>
        <Button
          title={t('createPassword.save')}
          isSmall
          disabled={setPassword.isPending}
          onPress={submit}
        />
        {onLater ? (
          <Button
            title={t('createPassword.later')}
            isSmall
            unfilled
            disabled={setPassword.isPending}
            onPress={onLater}
          />
        ) : null}
      </View>
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: { gap: theme.spacing.sm, width: '100%' },
    actions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
  })
